import { acss } from '@adornis/chemistry/directives/acss.js';
import type { GenericFieldFormController } from '@adornis/cms/client/generic-field-formcontroller.js';
import '@adornis/internationalization/client/language-picker/x-country-picker';
import '@adornis/internationalization/client/x-tel-input';
import { globalLanguageSubject } from '@adornis/translation-core/client/languageSubject.js';
import type { TranslationController } from '@adornis/translation-core/client/translation-controller.js';
import { html } from 'lit';
import type { BHTranslationDictionary } from '../../translations/translation.js';
import type { RegistrationUserDataPlugin } from '../plugin-types.js';
import { type IcsrmStammdaten } from '../stammdaten/icsrm-stammdaten-plugin.js';

export const icsrmRegistrationUserDataPlugin: RegistrationUserDataPlugin<IcsrmStammdaten> = {
  id: 'registration::user-data::icsrm',
  type: 'registration::user-data',
  render: (
    stContr: GenericFieldFormController<IcsrmStammdaten>,
    translationController: TranslationController<BHTranslationDictionary>,
  ) => {
    const genericFieldOption = (fieldName: string) =>
      ({
        placeholder: translationController.translate('icsrm', 'field', fieldName as any) as string,
        placeholderMode: 'static-floating',
      } as const);

    // helper
    const enumKeys = (ennuum: any) => Object.keys(ennuum).filter(key => !key.startsWith('_'));

    // components
    const textarea = (key: Parameters<typeof stContr.field>[0]) => html`
      <x-textarea
        placeholder=${translationController.translate('icsrm', 'field', key as any) + '*'}
        ${stContr.field(key)}
      ></x-textarea>
    `;

    const countryPicker = (key: Parameters<typeof stContr.field>[0]) => html`
      <x-country-picker
        showflag
        language=${globalLanguageSubject.getValue()}
        placeholder=${translationController.translate('icsrm', 'field', key as any) + '*'}
        ${stContr.field(key)}
      >
      </x-country-picker>
    `;

    const multiselect = (key: Parameters<typeof stContr.field>[0], selectables: string[]) => html`
      <tr-dropdown-multiselection
        ${stContr.field(key)}
        placeholder=${translationController.translate('icsrm', 'field', key as any) + '*'}
        .selectables=${selectables}
        .renderString=${key => translationController.translate('enums', key)}
      ></tr-dropdown-multiselection>
    `;

    return html`
      <x-flex space="lg">
        <!-- personal data -->
        <x-flex space="sm">
          ${stContr.genericField(['gender'], genericFieldOption('gender'))}
          <x-date-picker-dropdown
            placeholder=${translationController.translate('icsrm', 'field', 'dateOfBirth') + '*'}
            ${stContr.field('dateOfBirth')}
          ></x-date-picker-dropdown>

          <!-- nationality -->
          ${countryPicker('nationality')}

            <!-- country of birth -->
          ${countryPicker('countryOfBirth')}

          <!-- phone number -->
          <x-tel-input
            placeholder=${translationController.translate('icsrm', 'field', 'phoneNumber') + '*'}
            ${stContr.field('phoneNumber')}
          ></x-tel-input>

          <!-- addresse -->
          <x-flex space="md">
            <tr-h4> Adresse </tr-h4>
            <x-flex space="sm">
              <x-flex horizontal space="sm" ${acss({ '*:last-child': { flex: '1' } })}>
                ${stContr.genericField(['address', 'zipCity', 'zip'], genericFieldOption('zip'))}
                ${stContr.genericField(['address', 'zipCity', 'city'], genericFieldOption('city'))}
              </x-flex>
            </x-flex>

            <tr-dropdown-selection
              ${stContr.field('isImmigrant')}
              placeholder=${translationController.translate('icsrm', 'field', 'isImmigrant') + '*'}
              .selectables=${[true, false]}
              .renderString=${key => translationController.translate(key ? 'yes' : 'no')}
            ></tr-dropdown-selection>

            <tr-dropdown-selection
              ${stContr.field('foreignUniQualification')}
              placeholder=${translationController.translate('icsrm', 'field', 'foreignUniQualification') + '*'}
              .selectables=${[true, false]}
              .renderString=${key => translationController.translate(key ? 'yes' : 'no')}
            ></tr-dropdown-selection>
          </x-flex>
    </br>
          </x-flex>
    </br>

          <!-- studium -->
          <x-flex space="sm">
            <tr-h4> Studium </tr-h4>
            ${stContr.genericField(['currentUni'], genericFieldOption('currentUni'))}
            <tr-dropdown-selection
              ${stContr.field('semester')}
              placeholder=${translationController.translate('icsrm', 'field', 'semester') + '*'}
              .selectables=${[
                1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28,
                29, 30, 31, 32,
              ].map(i => i.toString())}
            ></tr-dropdown-selection>

            ${stContr.genericField(['courseOfStudy'], genericFieldOption('courseOfStudy'))}
            ${stContr.genericField(['department'], genericFieldOption('department'))}
            ${stContr.genericField(['desiredDegree'], genericFieldOption('desiredDegree'))}
          </x-flex>
        </x-flex>
      </x-flex>
    `;
  },
};
